import { ArrowRight } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { pickText } from "../../schema/core.ts";
import { ErrorState } from "../components/ErrorState.tsx";
import { useSiteCopy } from "../i18n/useSiteCopy.ts";
import { useCourseKit } from "../provider.tsx";
import { useCourses } from "../queries.ts";
import { coursePath, useDocumentTitle } from "../routing.ts";
import { EmptyState } from "../ui/Card.tsx";
import { cn } from "../ui/cn.ts";
import { PageLoading } from "../ui/Spinner.tsx";
import { CourseOverview } from "./CourseOverview.tsx";

/** `/`: the published courses; a single course is shown directly. */
export const HomePage = (): ReactNode => {
  const { locale } = useCourseKit();
  const copy = useSiteCopy();
  const courses = useCourses();
  const single = courses.data?.length === 1 ? courses.data[0] : undefined;
  // A single course titles the tab itself (CourseOverview).
  useDocumentTitle(single || courses.isPending ? null : copy.home.title);

  if (courses.isPending) return <PageLoading label={copy.common.loading} />;
  if (courses.isError) return <ErrorState onRetry={() => void courses.refetch()} />;
  if (single) return <CourseOverview slug={single.slug} />;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-12">
      <h1 className="text-4xl font-semibold sm:text-5xl">{copy.home.title}</h1>
      <p className="mt-3 max-w-[60ch] text-xl text-muted-foreground">{copy.home.intro}</p>
      {courses.data.length === 0 ? (
        <div className="mt-10">
          <EmptyState title={copy.home.emptyTitle} body={copy.home.emptyBody} />
        </div>
      ) : (
        <ul className="mt-10 grid gap-6 sm:grid-cols-2">
          {courses.data.map((course) => {
            const summary = pickText(course.summary, locale).trim();
            return (
              <li key={course.id}>
                <Link
                  to={coursePath(course.slug)}
                  className={cn(
                    "group flex h-full flex-col overflow-hidden rounded-3xl border border-border",
                    "bg-card transition-colors hover:border-primary",
                  )}>
                  {course.coverImage && (
                    <img
                      src={course.coverImage}
                      alt=""
                      loading="lazy"
                      className="aspect-[16/9] w-full bg-muted object-cover"
                    />
                  )}
                  <span className="flex flex-1 flex-col gap-2 p-5 sm:p-6">
                    <span
                      className={cn(
                        "font-display text-2xl leading-tight font-semibold",
                        "group-hover:underline",
                      )}>
                      {pickText(course.title, locale)}
                    </span>
                    {summary && <span className="text-muted-foreground">{summary}</span>}
                    <span
                      className={cn(
                        "mt-auto flex items-center justify-between gap-3 pt-3",
                        "text-base font-semibold text-primary",
                      )}>
                      {copy.home.lessonCount(course.lessonCount)}
                      <span className="inline-flex items-center gap-1">
                        {copy.home.viewCourse}
                        <ArrowRight aria-hidden="true" className="size-4" />
                      </span>
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
